import { fetchPrimary } from "../environments/primary/fetch";
import { ensureLocalApi } from "../localApi";
import { endLiveTalkForSignOut } from "../components/chat/LiveTalk";
import { beginNeucleosSignOut, redeemDeskSignInTicket, signOutWindowClerk, type LiveReport } from "./worldClerk";
import { isWebHost } from "../webHost";
import {
  hydrateWebHostWorlds,
  parsePendingRevocations,
  refreshNeucleosWorlds,
  type NeucleosPendingRevocation,
} from "./worldStore";

/**
 * The Neucleos sign-in and sign-out ceremony, presentation-neutral: the
 * loopback status read, the cockpit popup handoff with its settlement watch,
 * and the logout that revokes every deposited World grant. Settings V1,
 * Settings V2, the Desk dock, and the re-auth door all fold over this one
 * module so the eras cannot drift into different sign-in behavior — the
 * Settings V2 Account page once lost sign-out entirely because the ceremony
 * lived inside a legacy Settings panel V2 is forbidden to import.
 */

export interface NeucleosStatusWorld {
  readonly worldKey: string;
  readonly name: string;
  readonly mcpTokenPresent: boolean;
  readonly expiresAt: number;
}

export interface NeucleosStatus {
  readonly configured: boolean;
  readonly mcpEnabled: boolean;
  readonly worldKey: string;
  readonly signedIn: boolean;
  readonly identity: string | null;
  readonly mcpTokenPresent?: boolean;
  readonly worlds?: ReadonlyArray<NeucleosStatusWorld>;
  /** The Actor the stored sign-in is for. */
  readonly providerAccountActorId?: string | null;
  /** Can 31: where the window reads the World live, and what the World last answered it (worldClerk.ts). */
  readonly convexUrl?: string;
  readonly jwtTemplate?: string;
  readonly live?: LiveReport;
}

/** The status endpoint answers only loopback requests; through a Cleo
 * Connect tunnel it refuses with 403 loopback_only by design (the World
 * login handoff deposits its grant to the machine's loopback). That refusal
 * means "sign in on the Desk itself", not "login is unconfigured". */
export type NeucleosStatusRead = NeucleosStatus | "remote_refused" | null;

export async function readStatus(): Promise<NeucleosStatusRead> {
  const response = await fetchPrimary("/api/neucleos/status");
  if (response.status === 403) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    if (body?.error === "loopback_only") return "remote_refused";
    return null;
  }
  if (!response.ok) return null;
  return (await response.json()) as NeucleosStatus;
}

export function statusDescription(status: NeucleosStatusRead): string {
  if (status === "remote_refused") {
    return "Neucleos sign-in is available on the Desk itself, not from a remote session.";
  }
  if (!status?.configured) return "Production cockpit login is not configured.";
  const worlds = status.worlds ?? [];
  if (status.identity && worlds.length === 0) {
    return `Signed in as ${status.identity}. No active World memberships. Memberships appear after the next sign-in.`;
  }
  if (status.signedIn && status.mcpTokenPresent && worlds.length > 0) {
    const plural = worlds.length === 1 ? "World" : "Worlds";
    return `Signed in as ${status.identity ?? "a World member"}. ${worlds.length} ${plural} from your last sign-in.`;
  }
  return "Sign in to Neucleos to access your Worlds.";
}

/** Matches the server's LOGIN_ATTEMPT_TTL_MS: past it the attempt is dead. */
const LOGIN_SETTLE_DEADLINE_MS = 10 * 60 * 1000;
const LOGIN_SETTLE_POLL_MS = 2_000;

export function isSettledStatus(status: NeucleosStatusRead): boolean {
  return status !== "remote_refused" && Boolean(status?.signedIn && status.mcpTokenPresent);
}

// Public grant dates change when the authority replaces a deposited sign-in.
// An already valid sign-in cannot prove that a new sign-in completed.
function depositSnapshot(status: NeucleosStatusRead): string {
  if (status === null || status === "remote_refused") return JSON.stringify(status);
  return JSON.stringify([
    status.identity,
    [...(status.worlds ?? [])]
      .map((world) => [world.worldKey, world.expiresAt, world.mcpTokenPresent] as const)
      .sort((left, right) => left[0].localeCompare(right[0])),
  ]);
}

function isLoopbackCallbackOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "http:" &&
      (url.hostname === "127.0.0.1" || url.hostname === "localhost" || url.hostname === "::1")
    );
  } catch {
    return false;
  }
}

/**
 * Observe the deposit until the popup ceremony finishes. The callback's
 * loopback-origin message is a fast path; status polling remains the truth
 * because cross-origin navigation can sever the opener before that message.
 * A FIRST sign-in is a human ceremony (the Clerk modal plus the "Continue as"
 * click) that takes as long as the person takes, so completion is watched,
 * never scheduled. Give up only at the login attempt's own TTL. Returns the
 * stop function; `onDone` fires exactly once, however the watch ends.
 */
export function watchLoginSettlement(input: {
  readonly popup: LoginPopupHandle | null;
  readonly initialStatus: NeucleosStatusRead;
  readonly read: () => Promise<NeucleosStatusRead>;
  readonly onDone: () => void;
  /** Fires once, before `onDone`, only when the deposit actually landed. */
  readonly onSettled?: () => void;
}): () => void {
  const startedAt = Date.now();
  const initialDeposit = depositSnapshot(input.initialStatus);
  let closedSeen = false;
  // A cross-origin navigation may sever the WindowProxy before the person
  // finishes the hosted ceremony. Only a handle we positively saw OPEN can
  // later prove a user-driven close; null/closed-from-birth is unknowable.
  let observedOpen = input.popup !== null && !input.popup.closed;
  let reading = false;
  let stopped = false;
  let timer: number | undefined;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer !== undefined) window.clearInterval(timer);
    window.removeEventListener("message", onMessage);
    input.onDone();
  };
  const tick = async () => {
    if (stopped || reading) return;
    reading = true;
    try {
      if (Date.now() - startedAt >= LOGIN_SETTLE_DEADLINE_MS) {
        stop();
        return;
      }
      // Read the closed flag BEFORE the status read: the deposit is written
      // before the success page is served, so a read that starts after the
      // close already sees a landed deposit.
      const closedBeforeRead = input.popup === null || input.popup.closed;
      if (!closedBeforeRead) observedOpen = true;
      const status = await input.read();
      if (stopped) return;
      if (isSettledStatus(status) && depositSnapshot(status) !== initialDeposit) {
        input.onSettled?.();
        stop();
        return;
      }
      if (observedOpen && closedBeforeRead && closedSeen) {
        stop();
        return;
      }
      if (observedOpen && closedBeforeRead) closedSeen = true;
    } finally {
      reading = false;
    }
  };
  const onMessage = (event: MessageEvent) => {
    if (!isLoopbackCallbackOrigin(event.origin)) return;
    if (
      typeof event.data !== "object" ||
      event.data === null ||
      (event.data as { type?: unknown }).type !== "neucleos:login-complete"
    ) {
      return;
    }
    void tick();
  };
  timer = window.setInterval(() => {
    void tick();
  }, LOGIN_SETTLE_POLL_MS);
  window.addEventListener("message", onMessage);
  return stop;
}

/**
 * The settlement read every surface outside the Connections row shares: the
 * status endpoint is the truth the watcher folds over, and the World store
 * refresh is what flips signed-in UI (footer, avatar) the moment grants land.
 */
export async function readStatusRefreshingWorlds(): Promise<NeucleosStatusRead> {
  const next = await readStatus();
  await refreshNeucleosWorlds();
  return next;
}

/** What `window.open` hands back: a cross-origin WindowProxy still answers `closed` and `close()`. */
export type LoginPopupHandle = Pick<Window, "closed"> & Partial<Pick<Window, "close">>;

/**
 * The deposit landed, so the door has nothing left to show: close the popup
 * this Desk opened. A cross-origin WindowProxy honors `close()` for the window
 * that opened it, which the callback page itself cannot always do (a tab the
 * browser owns — the desktop app sends sign-in to the system browser — is
 * never script-closable once it has history). A handle without `close`, an
 * already-closed one, or a host that refuses is left alone.
 */
export function closeLoginPopup(popup: LoginPopupHandle | null): void {
  if (popup === null || typeof popup.close !== "function") return;
  try {
    if (!popup.closed) popup.close();
  } catch {
    // The page closes itself or says honestly that it cannot.
  }
}

/** Bring the Desk back in front: the desktop window on the Desk, `window.focus()` in a browser. */
export function returnToDesk(): void {
  try {
    void ensureLocalApi()
      .shell.focusWindow()
      .catch(() => undefined);
  } catch {
    // No local API on this host (a bare test window); the person finds the Desk themselves.
  }
}

/**
 * Start the cockpit sign-in handoff directly: fetch the authorize URL, open
 * it in the browser (the desktop app sends it to the system browser), and
 * watch settlement. Any surface (settings row, footer) can launch the same
 * ceremony. Once the deposit lands, the window signs its own Clerk in with
 * the deposit's one-time ticket (Can 31). Returns the watcher's stop function,
 * or null when login could not start; `onDone` fires exactly once when the
 * watch ends.
 */
export async function startNeucleosSignIn(input: {
  readonly read: () => Promise<NeucleosStatusRead>;
  readonly onDone: () => void;
}): Promise<(() => void) | null> {
  const initialStatus = await input.read();
  if (initialStatus === null || initialStatus === "remote_refused") return null;
  // Jason, 17 Oct 2026: always the hosted page in the browser, never a form in the Desk's window.
  const response = await fetchPrimary("/api/neucleos/login", {
    headers: { accept: "application/json" },
  });
  if (!response.ok) return null;
  const body = (await response.json()) as { authorizeUrl?: string };
  if (typeof body.authorizeUrl !== "string" || !body.authorizeUrl.startsWith("https://")) {
    return null;
  }
  const popup = window.open(body.authorizeUrl, "neucleos-login", "width=480,height=720");
  return watchLoginSettlement({
    popup,
    initialStatus,
    read: input.read,
    onDone: input.onDone,
    onSettled: () => {
      closeLoginPopup(popup);
      returnToDesk();
      void redeemDeskSignInTicket();
    },
  });
}

export interface SignOutOutcome {
  /** The loopback logout answered 200: custody is cleared on this Mac. */
  readonly ok: boolean;
  /** World grants the authority confirmed revoked. */
  readonly revoked: number;
  /** Grants the World has not confirmed revoked yet. The Desk keeps them and
   * retries until it does or they expire (W-2a2288b5). */
  readonly unrevoked: number;
  /** The Worlds whose access is still live, for the person to see. */
  readonly pending: ReadonlyArray<NeucleosPendingRevocation>;
  /** Plain words from the Desk when it could not keep the record of access
   * still to end (no usable key on this Mac, W-1f1bc930), or null. */
  readonly notice: string | null;
}

/**
 * Sign out on this Mac: the loopback logout revokes every deposited World
 * grant it can reach and clears the session custody; then the World store is
 * re-read so every signed-in face (dock, avatar, Settings) flips at once.
 */
export async function signOutOfNeucleos(): Promise<SignOutOutcome> {
  if (isWebHost()) return signOutOfWebHost();
  // W-2b8dcba8: no self sign-in or ticket redemption begun before this sign-out completes after it, and none begins
  // until the window's Clerk is ended (below).
  const endSignOut = beginNeucleosSignOut();
  try {
    return await signOutOfNeucleosUnderway();
  } finally {
    endSignOut();
  }
}

async function signOutOfNeucleosUnderway(): Promise<SignOutOutcome> {
  // An admitted Live call must End through the stop door while the grant that
  // admitted it still exists; only then is the grant dropped. Bounded: a stop
  // the World cannot confirm in time is left to the World's own observer, and
  // sign-out still proceeds so fresh access stops.
  try {
    await endLiveTalkForSignOut(8_000);
  } catch {
    // The World's observer reconciles a call whose stop could not be confirmed.
  }
  let outcome: SignOutOutcome = { ok: false, revoked: 0, unrevoked: 0, pending: [], notice: null };
  try {
    const response = await fetchPrimary("/api/neucleos/logout", {
      method: "POST",
    });
    if (response.ok) {
      const body = (await response.json().catch(() => null)) as {
        revoked?: unknown;
        unrevoked?: unknown;
        pending?: unknown;
        notice?: unknown;
      } | null;
      outcome = {
        ok: true,
        revoked: typeof body?.revoked === "number" ? body.revoked : 0,
        unrevoked: typeof body?.unrevoked === "number" ? body.unrevoked : 0,
        pending: parsePendingRevocations(body?.pending),
        notice: typeof body?.notice === "string" && body.notice ? body.notice : null,
      };
    }
  } catch {
    // The local server is unreachable; the store refresh below reports the truth.
  }
  // The window's Clerk session ends with the grants: left signed in, the Desk would sign itself straight back in.
  await signOutWindowClerk().catch(() => undefined);
  await refreshNeucleosWorlds();
  return outcome;
}

/**
 * cleo-web (webHost.ts, W-9f33f8b5): the browser holds no World grants and has no Mac server, so signing out is ending
 * the window's Clerk session (WebHostWorldSession.tsx registers it); the World store is signed out with it at once, and
 * the session effect confirms it when Clerk reports signed out. Not ok only when the window mounted no Clerk to end.
 */
async function signOutOfWebHost(): Promise<SignOutOutcome> {
  try {
    await endLiveTalkForSignOut(8_000);
  } catch {
    // The World's observer reconciles a call whose stop could not be confirmed.
  }
  let ok = false;
  try {
    ok = await signOutWindowClerk();
  } catch {
    ok = false;
  }
  if (ok) hydrateWebHostWorlds(null);
  return { ok, revoked: 0, unrevoked: 0, pending: [], notice: null };
}
